import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Search, Crown, Mail, Ban, CheckCircle, XCircle, LogOut, Check, Settings, Filter, Lock, AlertTriangle, Info, BadgeCheck, Database, TrendingUp, TrendingDown, RefreshCw, Calendar, FileText, ChevronDown, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { AdminMatchRevert } from "@/components/AdminMatchRevert";
import { useAdmin } from "@/hooks/useAdmin";
import { apiRequest } from "@/lib/queryClient";
import { useState, useEffect, useRef } from "react";
import { getActiveAlerts } from "@/lib/alerts";
import { XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell } from "recharts";
import { Users, Trophy, Building2, UserCheck, ShieldAlert, BellRing, Activity, Clock, RefreshCw as RefreshCwIcon, Ghost, UserX, AlertOctagon, UserMinus, Smartphone, Monitor, Tablet, Trash2 as Trash2Icon } from "lucide-react";

function AdminDashboard({ stats, isLoading, period, onPeriodChange, onRefresh, onContactExpired, onContactGhosts, onContactActive, onContactInactive, onContactNotifications, onContactInscrits }: {
  stats: any;
  isLoading: boolean;
  period: number;
  onPeriodChange: (p: number) => void;
  onRefresh: () => void;
  onContactExpired: () => void;
  onContactGhosts: () => void;
  onContactActive: () => void;
  onContactInactive: () => void;
  onContactNotifications: () => void;
  onContactInscrits: () => void;
}) {
  const kpis = stats?.kpis;
  const charts = stats?.charts;
  const effectivePeriod = period > 0 ? period : 30;

  // Fill missing days for registrations chart
  function fillDays(data: { day: string; count: number }[], days: number) {
    const map: Record<string, number> = {};
    (data || []).forEach(d => { map[d.day.slice(0, 10)] = d.count; });
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      result.push({ day: key.slice(5), count: map[key] || 0 });
    }
    return result;
  }

  // Fill missing days for matches chart (confirmed / pending / cancelled / abandoned)
  function fillMatchDays(data: { day: string; confirmed: number; pending: number; cancelled: number; abandoned: number }[], days: number) {
    const map: Record<string, { confirmed: number; pending: number; cancelled: number; abandoned: number }> = {};
    (data || []).forEach(d => { map[d.day.slice(0, 10)] = { confirmed: d.confirmed, pending: d.pending, cancelled: d.cancelled, abandoned: d.abandoned }; });
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      result.push({ day: key.slice(5), ...(map[key] || { confirmed: 0, pending: 0, cancelled: 0, abandoned: 0 }) });
    }
    return result;
  }

  const kpiCards = [
    { label: "Inscrits", value: kpis?.totalUsers, icon: Users, color: "text-blue-600", bg: "bg-blue-50", sub: period > 0 ? `sur ${period}j` : undefined, action: kpis?.totalUsers > 0 ? onContactInscrits : undefined },
    { label: "Joueurs actifs", value: kpis?.activePlayers, icon: UserCheck, color: "text-green-600", bg: "bg-green-50", sub: `sur ${effectivePeriod}j`, action: kpis?.activePlayers > 0 ? onContactActive : undefined },
    { label: "Parties jouées", value: kpis?.totalMatches, icon: Trophy, color: "text-yellow-600", bg: "bg-yellow-50", sub: period > 0 ? `sur ${period}j` : undefined },
    { label: "Groupes", value: kpis?.totalClubs, icon: Building2, color: "text-purple-600", bg: "bg-purple-50", sub: period > 0 ? `sur ${period}j` : undefined },
    { label: "Premium actifs", value: kpis?.premiumActive, icon: Crown, color: "text-orange-600", bg: "bg-orange-50" },
    { label: "Comptes suspendus", value: kpis?.suspendedUsers, icon: ShieldAlert, color: "text-red-600", bg: "bg-red-50" },
    { label: "Notifications activées", value: kpis?.notificationsActive, icon: BellRing, color: "text-teal-600", bg: "bg-teal-50", sub: kpis?.notificationsTotal ? `${Math.round((kpis.notificationsActive / kpis.notificationsTotal) * 100)}% des joueurs` : undefined, action: kpis?.notificationsActive > 0 ? onContactNotifications : undefined },
    { label: "ELO moyen", value: kpis?.avgElo, icon: Activity, color: "text-indigo-600", bg: "bg-indigo-50" },
    { label: "Inscriptions en attente", value: kpis?.pendingRegistrations, icon: Clock, color: "text-gray-600", bg: "bg-gray-50" },
    { label: "Joueurs fantômes", value: kpis?.ghostPlayers, icon: Ghost, color: "text-red-700", bg: "bg-red-50", sub: `0 partie depuis +${effectivePeriod}j`, action: kpis?.ghostPlayers > 0 ? onContactGhosts : undefined },
    { label: `Inactifs ${effectivePeriod}j`, value: kpis?.inactivePlayers30d, icon: UserMinus, color: "text-red-500", bg: "bg-red-50", sub: `ont joué, absents sur ${effectivePeriod}j`, action: kpis?.inactivePlayers30d > 0 ? onContactInactive : undefined },
    { label: "Parties abandonnées", value: kpis?.abandonedTotal, icon: AlertOctagon, color: "text-orange-600", bg: "bg-orange-50", sub: `sur ${effectivePeriod}j` },
    { label: "Inscriptions expirées", value: kpis?.expiredRegistrations, icon: UserX, color: "text-orange-500", bg: "bg-orange-50", sub: `perdus sur ${effectivePeriod}j`, action: kpis?.expiredRegistrations > 0 ? onContactExpired : undefined },
    { label: "Comptes supprimés", value: kpis?.deletedUsers, icon: Trash2Icon, color: "text-red-900", bg: "bg-red-100", sub: "joueurs anonymisés (UserDEL)" },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle>Dashboard</CardTitle>
            <CardDescription>Vue d'ensemble de l'activité de l'application</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(period)} onValueChange={v => onPeriodChange(Number(v))}>
              <SelectTrigger className="w-[110px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Tous</SelectItem>
                <SelectItem value="7">7 jours</SelectItem>
                <SelectItem value="15">15 jours</SelectItem>
                <SelectItem value="30">30 jours</SelectItem>
                <SelectItem value="90">90 jours</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
              <RefreshCwIcon size={14} className={isLoading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {kpiCards.map(({ label, value, icon: Icon, color, bg, sub, action }: any) => (
            <div key={label} className={`rounded-lg p-3 ${bg} flex items-start gap-3`}>
              <div className={`mt-0.5 ${color}`}><Icon size={18} /></div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">{label}{sub ? <span className="ml-1 text-gray-400">({sub})</span> : null}</p>
                <div className="flex items-center gap-2">
                  <p className={`text-xl font-bold ${color}`}>
                    {isLoading ? "..." : (value ?? "—")}
                  </p>
                  {action && !isLoading && value > 0 && (
                    <button onClick={action} className="text-gray-400 hover:text-blue-500 transition-colors" title="Contacter par email">
                      <Mail size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Nouvelles inscriptions</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={fillDays(charts?.dailyUsers, effectivePeriod)} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={Math.floor(effectivePeriod / 7)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <RechartsTooltip formatter={(v: any) => [v, "Inscriptions"]} />
                <Bar dataKey="count" fill="#3b82f6" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Parties</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={fillMatchDays(charts?.dailyMatches, effectivePeriod)} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={Math.floor(effectivePeriod / 7)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <RechartsTooltip formatter={(v: any, name: any) => [v, ({ confirmed: "Validées", pending: "En attente (<48h)", cancelled: "Annulées", abandoned: "Abandonnées (>48h)" } as any)[name] ?? name]} />
                <Legend formatter={(v) => (({ confirmed: "Validées", pending: "En attente (<48h)", cancelled: "Annulées", abandoned: "Abandonnées (>48h)" } as any)[v] ?? v)} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="confirmed" stackId="a" fill="#16a34a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="pending" stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]} />
                <Bar dataKey="cancelled" stackId="a" fill="#ef4444" radius={[0, 0, 0, 0]} />
                <Bar dataKey="abandoned" stackId="a" fill="#9ca3af" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie chart — registration sources */}
        {(() => {
          const sources = charts?.registrationSources || [];
          const PIE_COLORS = ["#9ca3af","#3b82f6","#f59e0b","#8b5cf6","#ef4444","#06b6d4","#f97316","#ec4899","#84cc16","#6366f1"];
          const total = sources.reduce((s: number, d: any) => s + d.count, 0);
          const allOrganic = sources.length === 0 || (sources.length === 1 && sources[0].source === "organic");
          return (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Provenance des inscriptions (sur {effectivePeriod}j)</p>
              {allOrganic ? (
                <p className="text-sm text-gray-400 italic">Aucun UTM tracé sur cette période — toutes les inscriptions sont organiques.</p>
              ) : (
                <div className="flex flex-col lg:flex-row items-center gap-4">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={sources} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={90}
                        label={(props: any) => `${Math.round(props.count / total * 100)}%`} labelLine={false}>
                        {sources.map((_: any, i: number) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(v: any, _: any, props: any) => [`${v} inscriptions (${Math.round(v / total * 100)}%)`, props.payload.source]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-col gap-1 min-w-[160px]">
                    {sources.map((d: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="truncate text-gray-700">{d.source}</span>
                        <span className="ml-auto text-gray-500 font-medium">{d.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Pie chart — matches by club */}
        {charts?.matchesByClub?.length > 0 && (() => {
          const PIE_COLORS = ["#16a34a","#3b82f6","#f59e0b","#8b5cf6","#ef4444","#06b6d4","#f97316","#ec4899","#84cc16","#6366f1"];
          const total = charts.matchesByClub.reduce((s: number, d: any) => s + d.count, 0);
          return (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Parties validées par groupe (sur {effectivePeriod}j)</p>
              <div className="flex flex-col lg:flex-row items-center gap-4">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={charts.matchesByClub}
                      dataKey="count"
                      nameKey="club_name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={(props: any) => `${Math.round(props.count / total * 100)}%`}
                      labelLine={false}
                    >
                      {charts.matchesByClub.map((_: any, i: number) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(v: any, _: any, props: any) => [`${v} parties (${Math.round(v / total * 100)}%)`, props.payload.club_name]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col gap-1 min-w-[160px]">
                  {charts.matchesByClub.map((d: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="truncate text-gray-700">{d.club_name}</span>
                      <span className="ml-auto text-gray-500 font-medium">{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Platform pie chart */}
        {charts?.platforms?.length > 0 && (() => {
          const PLATFORM_COLORS: Record<string, string> = { ios: "#6366f1", android: "#16a34a", web: "#3b82f6" };
          const PLATFORM_LABELS: Record<string, string> = { ios: "iOS", android: "Android", web: "Web" };
          const total = charts.platforms.reduce((s: number, d: any) => s + d.count, 0);
          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Répartition par plateforme (tous joueurs)</p>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={charts.platforms} dataKey="count" nameKey="platform" cx="50%" cy="50%" outerRadius={80}
                      label={(props: any) => `${Math.round(props.count / total * 100)}%`} labelLine={false}>
                      {charts.platforms.map((d: any, i: number) => (
                        <Cell key={i} fill={PLATFORM_COLORS[d.platform] || "#9ca3af"} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(v: any, _: any, props: any) => {
                      const d = props.payload;
                      const activePct = d.count > 0 ? Math.round(d.active_count / d.count * 100) : 0;
                      return [`${v} joueurs (${Math.round(v / total * 100)}%) — ${d.active_count} notifiés (${activePct}%)`, PLATFORM_LABELS[d.platform] || d.platform];
                    }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col gap-2">
                {charts.platforms.map((d: any, i: number) => {
                  const activePct = d.count > 0 ? Math.round(d.active_count / d.count * 100) : 0;
                  return (
                    <div key={i} className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-3 text-sm">
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: PLATFORM_COLORS[d.platform] || "#9ca3af" }} />
                        <span className="text-gray-700 font-medium">{PLATFORM_LABELS[d.platform] || d.platform}</span>
                        <span className="ml-auto text-gray-500">{d.count} joueurs</span>
                        <span className="text-gray-400 w-10 text-right">{Math.round(d.count / total * 100)}%</span>
                      </div>
                      <div className="flex items-center gap-2 pl-6 text-xs text-gray-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                        <span>{d.active_count} notifiés ({activePct}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Matches per player distribution */}
        {charts?.matchesPerPlayer?.length > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Répartition parties / joueur (sur {effectivePeriod}j, hors 0)</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={charts.matchesPerPlayer} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <RechartsTooltip formatter={(v: any) => [v, "Joueurs"]} labelFormatter={(l) => `${l} partie(s)`} />
                <Bar dataKey="players" fill="#f97316" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Connexions — bar chart + last 10 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Joueurs connectés / jour (sur {effectivePeriod}j)</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={fillDays(charts?.dailyLogins, effectivePeriod)} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={Math.floor(effectivePeriod / 7)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <RechartsTooltip formatter={(v: any) => [v, "Connexions"]} />
                <Bar dataKey="count" fill="#6366f1" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">10 dernières connexions</p>
            <div className="space-y-1">
              {(stats?.lastLogins || []).length === 0 ? (
                <p className="text-xs text-gray-400">Aucune connexion enregistrée</p>
              ) : (
                (stats?.lastLogins || []).map((l: any, i: number) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-50">
                    <span className="font-medium text-gray-700 truncate max-w-[140px]">{l.first_name} {l.last_name}</span>
                    <span className="text-gray-400 ml-2 shrink-0 flex items-center gap-1">
                      {l.platform === 'ios' && <Tablet size={11} className="text-indigo-400" />}
                      {l.platform === 'android' && <Smartphone size={11} className="text-green-500" />}
                      {l.platform === 'web' && <Monitor size={11} className="text-blue-400" />}
                      {new Date(l.last_login_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}
                      {" "}
                      <span className="text-gray-300">{new Date(l.last_login_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminWebsiteStats({ stats, isLoading, period, onPeriodChange, onRefresh }: {
  stats: any;
  isLoading: boolean;
  period: number;
  onPeriodChange: (p: number) => void;
  onRefresh: () => void;
}) {
  const effectivePeriod = period > 0 ? period : 30;

  function fillDays(data: { day: string; count: number }[], days: number) {
    const map: Record<string, number> = {};
    (data || []).forEach(d => { map[d.day.slice(0, 10)] = d.count; });
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      result.push({ day: key.slice(5), count: map[key] || 0 });
    }
    return result;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle>Site web</CardTitle>
            <CardDescription>Visites du site vitrine (web.padelo.fr)</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(period)} onValueChange={v => onPeriodChange(Number(v))}>
              <SelectTrigger className="w-[110px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 jours</SelectItem>
                <SelectItem value="15">15 jours</SelectItem>
                <SelectItem value="30">30 jours</SelectItem>
                <SelectItem value="90">90 jours</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
              <RefreshCwIcon size={14} className={isLoading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg p-3 bg-blue-50 flex items-start gap-3">
            <div className="mt-0.5 text-blue-600"><Activity size={18} /></div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">Visites totales<span className="ml-1 text-gray-400">(sur {effectivePeriod}j)</span></p>
              <p className="text-xl font-bold text-blue-600">{isLoading ? "..." : (stats?.totalVisits ?? "—")}</p>
            </div>
          </div>
          <div className="rounded-lg p-3 bg-green-50 flex items-start gap-3">
            <div className="mt-0.5 text-green-600"><Users size={18} /></div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">Visiteurs uniques<span className="ml-1 text-gray-400">(sur {effectivePeriod}j)</span></p>
              <p className="text-xl font-bold text-green-600">{isLoading ? "..." : (stats?.uniqueVisitors ?? "—")}</p>
            </div>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-foreground mb-2">Visites par jour</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={fillDays(stats?.dailyVisits, effectivePeriod)} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={Math.floor(effectivePeriod / 7)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <RechartsTooltip formatter={(v: any) => [v, "Visites"]} />
              <Bar dataKey="count" fill="#3b82f6" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <VisitBreakdownTable
            title={`Pages visitées (sur ${effectivePeriod}j)`}
            columnLabel="Page"
            rows={(stats?.topPages || []).map((d: { path: string; count: number }) => ({ key: d.path, ...describePage(d.path), count: d.count }))}
          />
          <VisitBreakdownTable
            title={`Provenance des visites (sur ${effectivePeriod}j)`}
            columnLabel="Provenance"
            rows={(stats?.topReferrers || []).map((d: { referrer: string; count: number }) => ({ key: d.referrer, ...describeReferrer(d.referrer), count: d.count }))}
          />
        </div>

        <DownloadClicksTable clicks={stats?.downloadClicks || []} period={effectivePeriod} />
      </CardContent>
    </Card>
  );
}

// Human-readable labels for website tracking data
function describePage(path: string): { label: string; detail: string } {
  if (path === "/telecharger/") {
    return { label: "Page de téléchargement (site vitrine)", detail: "web.padelo.fr/telecharger/ — liens padelo.fr/idownload et padelo.fr/telecharger" };
  }
  if (path === "/telecharger") {
    return { label: "Lien de téléchargement serveur", detail: "app.padelo.fr/telecharger — redirection directe" };
  }
  if (path === "/" || path === "/index.html") {
    return { label: "Accueil du site vitrine", detail: "web.padelo.fr" };
  }
  return { label: path, detail: "" };
}

function describeReferrer(referrer: string): { label: string; detail: string } {
  if (referrer === "Direct") {
    return { label: "Direct / inconnue", detail: "Aucune page d'origine transmise : lien tapé, QR code, appli Instagram (iPhone)…" };
  }
  if (/instagram\.com/i.test(referrer)) {
    return { label: "Instagram", detail: referrer };
  }
  if (/^android-app:\/\/com\.google\.android\.googlequicksearchbox/i.test(referrer)) {
    return { label: "Appli Google (Android)", detail: "Recherche Google ou Discover" };
  }
  if (/^android-app:\/\//i.test(referrer)) {
    return { label: "Appli Android", detail: referrer.replace(/^android-app:\/\//i, "") };
  }
  if (/(^|\.)google\./i.test(referrer.replace(/^https?:\/\//, ""))) {
    return { label: "Recherche Google", detail: referrer };
  }
  if (/facebook\.com|fb\.com/i.test(referrer)) {
    return { label: "Facebook", detail: referrer };
  }
  try {
    return { label: new URL(referrer).hostname, detail: referrer };
  } catch {
    return { label: referrer, detail: "" };
  }
}

function VisitBreakdownTable({ title, columnLabel, rows }: {
  title: string;
  columnLabel: string;
  rows: { key: string; label: string; detail: string; count: number }[];
}) {
  const total = rows.reduce((s, r) => s + r.count, 0);
  return (
    <div>
      <p className="text-sm font-medium text-foreground mb-2">{title}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">Aucune donnée sur cette période.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="text-left py-2 pr-4 font-medium">{columnLabel}</th>
                <th className="text-right py-2 px-2 font-medium">Visites</th>
                <th className="text-right py-2 pl-2 font-medium">%</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.key} className="border-b border-border last:border-0 align-top">
                  <td className="py-2 pr-4">
                    <div className="text-foreground">{r.label}</div>
                    {r.detail && <div className="text-xs text-muted-foreground break-all">{r.detail}</div>}
                  </td>
                  <td className="text-right py-2 px-2 font-semibold text-foreground">{r.count}</td>
                  <td className="text-right py-2 pl-2 text-muted-foreground">{total ? Math.round(r.count / total * 100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const DOWNLOAD_DESTINATIONS = [
  { key: "ios", label: "App Store" },
  { key: "android", label: "Play Store" },
  { key: "web", label: "Web" },
];

const DOWNLOAD_SOURCES: Record<string, { label: string; detail: string }> = {
  instagram: { label: "Instagram", detail: "padelo.fr/idownload" },
  old_campaign: { label: "Ancienne campagne", detail: "padelo.fr/telecharger" },
  direct: { label: "Sans source", detail: "lien sans utm_source" },
};

function DownloadClicksTable({ clicks, period }: {
  clicks: { source: string; destination: string; count: number }[];
  period: number;
}) {
  // Pivot rows (source, destination, count) into one row per campaign source
  const bySource: Record<string, Record<string, number>> = {};
  clicks.forEach(c => {
    bySource[c.source] = bySource[c.source] || {};
    bySource[c.source][c.destination] = (bySource[c.source][c.destination] || 0) + c.count;
  });
  const rows = Object.entries(bySource)
    .map(([source, counts]) => ({ source, counts, total: Object.values(counts).reduce((s, n) => s + n, 0) }))
    .sort((a, b) => b.total - a.total);

  return (
    <div>
      <p className="text-sm font-medium text-foreground mb-1">Clics sur le lien de téléchargement par source (sur {period}j)</p>
      <p className="text-xs text-muted-foreground mb-2">Source = lien court utilisé (UTM), destination = où le visiteur a été redirigé selon son appareil.</p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">Aucun clic sur cette période.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="text-left py-2 pr-4 font-medium">Source</th>
                {DOWNLOAD_DESTINATIONS.map(d => (
                  <th key={d.key} className="text-right py-2 px-2 font-medium">{d.label}</th>
                ))}
                <th className="text-right py-2 pl-2 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const source = DOWNLOAD_SOURCES[r.source] || { label: r.source, detail: "" };
                return (
                  <tr key={r.source} className="border-b border-border last:border-0 align-top">
                    <td className="py-2 pr-4">
                      <div className="text-foreground">{source.label}</div>
                      {source.detail && <div className="text-xs text-muted-foreground">{source.detail}</div>}
                    </td>
                    {DOWNLOAD_DESTINATIONS.map(d => (
                      <td key={d.key} className="text-right py-2 px-2 text-muted-foreground">{r.counts[d.key] || 0}</td>
                    ))}
                    <td className="text-right py-2 pl-2 font-semibold text-foreground">{r.total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AlertSettingsForm({ settings, onSave, isLoading, playerCount }: { settings: any, onSave: (settings: any) => void, isLoading: boolean, playerCount: number }) {
  const [formData, setFormData] = useState({
    weeklyMatchesLimit: 5,
    monthlyMatchesLimit: 20,
    bagelsLimit: 10,
    bestBroWinRate: 90,
    bestBroMatches: 10,
    emailAlertsEnabled: false,
    emailRecipient: "",
    alertCooldownHours: 48,
    spreeLimit: 10,
    autoLifetimePremium: false,
    minVersion: "1.0.0",
    whatsNewVersion: "",
    whatsNewContentFr: "",
    whatsNewContentEn: "",
    whatsNewContentEs: "",
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        weeklyMatchesLimit: settings.weeklyMatchesLimit ?? 5,
        monthlyMatchesLimit: settings.monthlyMatchesLimit ?? 20,
        bagelsLimit: settings.bagelsLimit ?? 10,
        bestBroWinRate: settings.bestBroWinRate ?? 90,
        bestBroMatches: settings.bestBroMatches ?? 10,
        emailAlertsEnabled: settings.emailAlertsEnabled ?? false,
        emailRecipient: settings.emailRecipient ?? "",
        alertCooldownHours: settings.alertCooldownHours ?? 48,
        spreeLimit: settings.spreeLimit ?? 10,
        autoLifetimePremium: settings.autoLifetimePremium ?? false,
        minVersion: settings.minVersion ?? "1.0.0",
        whatsNewVersion: settings.whatsNewVersion ?? "",
        whatsNewContentFr: settings.whatsNewContentFr ?? "",
        whatsNewContentEn: settings.whatsNewContentEn ?? "",
        whatsNewContentEs: settings.whatsNewContentEs ?? "",
      });
    }
  }, [settings]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Weekly Matches Limit</Label>
          <Input type="number" value={formData.weeklyMatchesLimit} onChange={e => handleChange('weeklyMatchesLimit', parseInt(e.target.value) || 0)} />
          <p className="text-xs text-gray-500">Max matches per week before alert.</p>
        </div>
        <div className="space-y-2">
          <Label>Monthly Matches Limit</Label>
          <Input type="number" value={formData.monthlyMatchesLimit} onChange={e => handleChange('monthlyMatchesLimit', parseInt(e.target.value) || 0)} />
          <p className="text-xs text-gray-500">Max matches per month before alert.</p>
        </div>
        <div className="space-y-2">
          <Label>Bagels Limit (6-0 sets)</Label>
          <Input type="number" value={formData.bagelsLimit} onChange={e => handleChange('bagelsLimit', parseInt(e.target.value) || 0)} />
          <p className="text-xs text-gray-500">Max bagels received before alert.</p>
        </div>
        <div className="space-y-2">
          {/* New: Spree Limit Configuration */}
          <Label>Spree Limit (Streak)</Label>
          <Input type="number" value={formData.spreeLimit} onChange={e => handleChange('spreeLimit', parseInt(e.target.value) || 0)} />
          <p className="text-xs text-gray-500">Max winning/losing streak before alert.</p>
        </div>
        <div className="space-y-2">
          <Label>Alert Cooldown (Hours)</Label>
          <Input type="number" value={formData.alertCooldownHours} onChange={e => handleChange('alertCooldownHours', parseInt(e.target.value) || 0)} />
          <p className="text-xs text-gray-500">Minimum hours between email alerts for the same player.</p>
        </div>
        <div className="space-y-2">
          <Label>Best Bro Win Rate Threshold (%)</Label>
          <Input type="number" value={formData.bestBroWinRate} onChange={e => handleChange('bestBroWinRate', parseInt(e.target.value) || 0)} />
          <p className="text-xs text-gray-500">Win rate with best partner to trigger alert.</p>
        </div>
        <div className="space-y-2">
          <Label>Best Bro Matches Threshold</Label>
          <Input type="number" value={formData.bestBroMatches} onChange={e => handleChange('bestBroMatches', parseInt(e.target.value) || 0)} />
          <p className="text-xs text-gray-500">Min matches with partner to consider check.</p>
        </div>
        <div className="space-y-2">
          <Label>Minimum App Version</Label>
          <Input type="text" value={formData.minVersion} onChange={e => handleChange('minVersion', e.target.value)} />
          <p className="text-xs text-gray-500">Global minimum App version (e.g., "1.2.0")</p>
        </div>
      </div>

      <div className="border rounded-lg p-4 space-y-4 bg-white">
        <div>
          <h3 className="text-base font-semibold">Nouveautés (What's New)</h3>
          <p className="text-xs text-gray-500">Popup affichée une seule fois après mise à jour. Laisser vide pour désactiver.</p>
        </div>
        <div className="space-y-2">
          <Label>Version concernée</Label>
          <Input
            type="text"
            placeholder="ex: 1.43.0"
            value={formData.whatsNewVersion}
            onChange={e => handleChange('whatsNewVersion', e.target.value)}
          />
        </div>
        {(['Fr', 'En', 'Es'] as const).map(lang => {
          const field = `whatsNewContent${lang}` as 'whatsNewContentFr' | 'whatsNewContentEn' | 'whatsNewContentEs';
          const val = formData[field];
          return (
            <div key={lang} className="space-y-1">
              <div className="flex justify-between items-center">
                <Label>{lang.toUpperCase()}</Label>
                <span className={`text-xs ${val.length > 260 ? 'text-red-500' : 'text-gray-400'}`}>{val.length}/280</span>
              </div>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                rows={5}
                maxLength={280}
                placeholder={lang === 'Fr' ? "✨ v1.43 — Nouveautés\n\n• Nouveauté 1\n• Nouveauté 2\n\nBonne session ! 🎾" : ""}
                value={val}
                onChange={e => handleChange(field, e.target.value)}
              />
            </div>
          );
        })}
      </div>

      <div className="flex items-center space-x-2 border p-4 rounded-md bg-white">
        <Switch checked={formData.emailAlertsEnabled} onCheckedChange={c => handleChange('emailAlertsEnabled', c)} />
        <div>
          <Label className="text-base font-semibold">Enable Email Alerts</Label>
          <p className="text-sm text-gray-500">Send email notifications when thresholds are exceeded.</p>
        </div>
      </div>

      <div className="flex items-center space-x-2 border p-4 rounded-md bg-white">
        <Switch checked={formData.autoLifetimePremium} onCheckedChange={c => handleChange('autoLifetimePremium', c)} />
        <div>
          <Label className="text-base font-semibold">Premium à vie (Nouveaux utilisateurs - {playerCount} actuels)</Label>
          <p className="text-sm text-gray-500">Si activé, les nouveaux inscrits auront le Premium à Vie automatiquement. Sinon, ils auront 6 mois.</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Email Recipient</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <Input className="pl-10" value={formData.emailRecipient} onChange={e => handleChange('emailRecipient', e.target.value)} placeholder="admin@example.com" />
        </div>
        <p className="text-xs text-gray-500">Only one email address supported.</p>
      </div>

      <Button onClick={() => onSave(formData)} disabled={isLoading} className="bg-padel-green hover:bg-padel-green-light text-white">
        {isLoading ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
}

function langFlag(lang?: string): string {
  if (lang === "fr") return "🇫🇷";
  if (lang === "es") return "🇪🇸";
  if (lang === "en") return "🇬🇧";
  return "🌐";
}

export default function AdminPage() {
  const {
    isAuthenticated,
    searchQuery,
    setSearchQuery,
    premiumDialogOpen,
    setPremiumDialogOpen,
    suspensionDialogOpen,
    setSuspensionDialogOpen,
    selectedPlayer,
    setSelectedPlayer,
    premiumType,
    setPremiumType,
    premiumEndDate,
    setPremiumEndDate,
    suspensionReason,
    setSuspensionReason,
    suspendedUntil,
    setSuspendedUntil,
    emailFilter,
    setEmailFilter,
    suspendedFilter,
    setSuspendedFilter,
    premiumFilter,
    setPremiumFilter,
    statsFilter,
    setStatsFilter,
    players,
    filteredPlayers,
    isLoading,
    adminForm,
    adminLoginMutation,
    activateAccountMutation,
    updatePremiumMutation,
    suspendAccountMutation,
    unsuspendAccountMutation,
    handleOpenPremiumDialog,
    handleOpenSuspensionDialog,
    handleSuspendAccount,
    handleUpdatePremium,
    calculateEndDate,
    handleLogout,
    handleAdminLogin,
    alertSettings,
    isLoadingAlerts,
    updateAlertSettingsMutation,

    deleteAccountMutation,
    triggerRecapSnapshotMutation,
    triggerRecapEmailMutation,
    sendBroadcastEmailMutation,
    updatePreferencesMutation,

    // ELO state and mutators
    eloDialogOpen,
    setEloDialogOpen,
    newEloRating,
    setNewEloRating,
    handleOpenEloDialog,
    updateEloMutation,
    // Clubs
    adminClubs,
    isLoadingClubs,
    updateClubVerificationMutation,
    deleteClubAdminMutation,
    sendSeasonRecapMutation,
    // FFT
    fttSearchQuery,
    setFttSearchQuery,
    fttStatus,
    isLoadingFttStatus,
    fttSearchResults,
    hasMoreFtt,
    loadMoreFttResults,
    isSearchingFtt,
    syncPlayersWithFttMutation,
    fttSettings,
    updateFttSettingsMutation,
    // Dashboard Stats
    adminStats,
    isLoadingStats,
    refetchStats,
    statsPeriod,
    setStatsPeriod,
    // Website Stats
    websiteStats,
    isLoadingWebsiteStats,
    refetchWebsiteStats,
    // Deleted players
    deletedPlayers,
    isLoadingDeletedPlayers,
    restoreDialogOpen,
    setRestoreDialogOpen,
    selectedDeletedPlayer,
    setSelectedDeletedPlayer,
    restoreUserId,
    setRestoreUserId,
    restoreName,
    setRestoreName,
    restoreDeletedPlayerMutation,
    deleteUserDelMutation,
  } = useAdmin();




  const EMAIL_TEMPLATES = [
    {
      id: "expired_registration",
      label: "Relance inscription expirée",
      subject: "⚠️ Ne laisse pas passer ton accès Premium à vie sur PadELO !",
      message: `Salut,

Nous avons remarqué que tu as commencé ton aventure PadELO l'autre jour, mais que tu n'as pas finalisé la dernière étape : la validation de ton inscription avec le code que tu as reçu.

Ne t'inquiète pas, c'est super simple à corriger. Il te suffit de recommencer la procédure d'inscription. Entre à nouveau ton adresse email sur l'app, et nous t'enverrons instantanément un tout nouveau code de validation. Une fois saisi, tu seras connecté !

⚠️ Pourquoi tu ne dois pas traîner :

Nous avons décidé d'offrir l'accès Premium à vie exclusivement aux tout premiers membres de la communauté PadELO. C'est notre façon de vous remercier de croire au projet dès le début.

Cependant, cette offre est strictement limitée en nombre. En finalisant ton inscription dès maintenant, tu t'assures de bloquer ton statut Premium gratuitement pour toujours, avant qu'il ne soit trop tard.

Une fois connecté, tu pimenteras instantanément tes parties avec le classement Elo, tes statistiques "Pro" et la gestion de tes clubs/ligues.

Allez, on se voit sur les pistes !

À très vite,

Jonathan
Fondateur de PadELO
<a href="https://www.padelo.fr">www.padelo.fr</a>`,
    },
    {
      id: "ghost_player",
      label: "Relance joueur fantôme (0 match)",
      subject: "👻 Ton compteur PadELO est à zéro ? Ne loupe pas le coche !",
      message: `Salut,

Inscrit, mais toujours 0 match joué ? Tu rates l'essentiel.

⚠️ Urgent : Bloque ton accès Premium à vie GRATUITEMENT.
Cette offre est strictement limitée aux tout premiers membres actifs. Valide ton premier match maintenant pour assurer ton statut Premium gratuit pour toujours.

Pimente instantanément tes parties avec :

📈 Ton vrai niveau Elo : Prouve ton rang réel.
📊 Des statistiques puissantes : Taux de réussite gauche/droite, pourcentage de victoires avec chaque partenaire et contre tous tes adversaires.
⭐️ Nouveau : Classement FFT : Ton évolution officielle mise à jour chaque mois dans l'app.

<b>Tes partenaires ne sont pas encore sur l'arène ?</b>
Invite-les dès aujourd'hui pour valider cette première partie ensemble !

On se voit sur les pistes,

Jonathan
Fondateur de PadELO
<a href="https://www.padelo.fr">www.padelo.fr</a>`,
    },
    {
      id: "store_review",
      label: "Demande de note sur les stores (FR)",
      subject: "Un petit mot du créateur de PadELO 🎾",
      // Blocs HTML sur une seule ligne : chaque retour à la ligne devient un <br> à l'envoi
      message: `<div style="font-family:Arial,sans-serif;font-size:16px;line-height:1.6;">Bonjour,

Je suis Jonathan. Avec des amis passionnés de padel, on s'est lancés dans un projet un peu fou : l'app que nous rêvions d'avoir en tant que joueurs. Pas de grosse boîte derrière nous, juste beaucoup de soirées et de week-ends :)

Vous utilisez PadELO depuis quelques semaines. Si elle vous est utile, une note sur le store nous aiderait énormément à la faire connaître aux autres joueurs. Cela prend 10 secondes, promis.

<div style="text-align:center;margin:8px 0;"><a href="https://app.padelo.fr/api/noter?utm_source=email_notation" style="display:inline-block;padding:14px 28px;background:#16a34a;color:#ffffff;text-decoration:none;border-radius:8px;font-weight:bold;">⭐ Noter PadELO</a></div>
Et si quelque chose ne va pas ou vous manque, répondez à ce mail. Je lis tout, vraiment.

Merci, et bons matchs !

<table cellpadding="0" cellspacing="0" border="0"><tr><td style="padding-right:16px;"><img src="https://app.padelo.fr/email-assets/jonathan.jpg" width="120" height="120" alt="Jonathan" style="display:block;border-radius:50%;width:120px;height:120px;"></td><td style="font-family:Arial,sans-serif;font-size:16px;line-height:1.5;"><b>Jonathan</b><br>Fondateur de PadELO<br><a href="https://www.padelo.fr">www.padelo.fr</a></td></tr></table></div>`,
    },
  ];

  const [activeTab, setActiveTab] = useState("dashboard");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [utmCopied, setUtmCopied] = useState(false);

  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastTarget, setBroadcastTarget] = useState<"all" | "specific" | "raw_emails">("all");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [rawEmailRecipients, setRawEmailRecipients] = useState<{ first_name: string; last_name: string; email: string; language?: string }[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");

  const [recapTarget, setRecapTarget] = useState<"all" | "specific">("all");
  const [recapSelectedUserIds, setRecapSelectedUserIds] = useState<string[]>([]);
  const [recapUserSearchQuery, setRecapUserSearchQuery] = useState("");

  // Season recap admin state
  const [selectedRecapClubId, setSelectedRecapClubId] = useState<string>("");
  const [selectedRecapSeasonId, setSelectedRecapSeasonId] = useState<string>("");

  // "lang:fr" filtre par langue de l'utilisateur, sinon recherche sur nom/email
  const isBroadcastCandidate = (p: NonNullable<typeof players>[number]) => {
    if (!p.user?.isVerified || p.user?.acceptsMarketingEmails === false) return false;
    const q = userSearchQuery.trim().toLowerCase();
    if (q.startsWith("lang:")) return p.user.language === q.slice(5);
    return !q || p.name.toLowerCase().includes(q) || p.user.email.toLowerCase().includes(q);
  };

  const handleSendBroadcast = () => {
    if (!broadcastSubject || !broadcastMessage) return;
    if (broadcastTarget === "specific" && selectedUserIds.length === 0) return;
    if (broadcastTarget === "raw_emails" && rawEmailRecipients.length === 0) return;

    const payload: any = {
      subject: broadcastSubject,
      message: broadcastMessage.replace(/\n/g, "<br>")
    };

    if (broadcastTarget === "specific") {
      payload.userIds = selectedUserIds;
    } else if (broadcastTarget === "raw_emails") {
      payload.rawEmails = rawEmailRecipients.map(r => r.email);
    }

    sendBroadcastEmailMutation.mutate(payload, {
      onSuccess: () => {
        setBroadcastSubject("");
        setBroadcastMessage("");
        setSelectedUserIds([]);
        setRawEmailRecipients([]);
        setBroadcastTarget("all");
      }
    });
  };

  const handleContactInscrits = async () => {
    try {
      const res = await apiRequest('GET', `/api/admin/recent-registrations?period=${statsPeriod}`);
      const data = await res.json();
      setRawEmailRecipients(data);
      setBroadcastTarget("raw_emails");
      setBroadcastSubject("");
      setBroadcastMessage("");
      setActiveTab("communications");
    } catch (e) {
      console.error("Failed to fetch recent registrations", e);
    }
  };

  const handleContactExpired = async () => {
    try {
      const res = await apiRequest('GET', `/api/admin/expired-registrations?period=${statsPeriod}`);
      const data = await res.json();
      setRawEmailRecipients(data);
      setBroadcastTarget("raw_emails");
      const tpl = EMAIL_TEMPLATES.find(t => t.id === "expired_registration")!;
      setBroadcastSubject(tpl.subject);
      setBroadcastMessage(tpl.message);
      setActiveTab("communications");
    } catch (e) {
      console.error("Failed to fetch expired registrations", e);
    }
  };

  const handleContactActive = async () => {
    try {
      const res = await apiRequest('GET', `/api/admin/active-players-list?period=${statsPeriod}`);
      const data = await res.json();
      setRawEmailRecipients(data);
      setBroadcastTarget("raw_emails");
      setBroadcastSubject("");
      setBroadcastMessage("");
      setActiveTab("communications");
    } catch (e) {
      console.error("Failed to fetch active players", e);
    }
  };

  const handleContactInactive = async () => {
    try {
      const res = await apiRequest('GET', `/api/admin/inactive-players-list?period=${statsPeriod}`);
      const data = await res.json();
      setRawEmailRecipients(data);
      setBroadcastTarget("raw_emails");
      setBroadcastSubject("");
      setBroadcastMessage("");
      setActiveTab("communications");
    } catch (e) {
      console.error("Failed to fetch inactive players", e);
    }
  };

  const handleContactNotifications = async () => {
    try {
      const res = await apiRequest('GET', '/api/admin/players-with-notifications');
      const data = await res.json();
      setRawEmailRecipients(data);
      setBroadcastTarget("raw_emails");
      setBroadcastSubject("");
      setBroadcastMessage("");
      setActiveTab("communications");
    } catch (e) {
      console.error("Failed to fetch players with notifications", e);
    }
  };

  const handleContactGhosts = async () => {
    try {
      const res = await apiRequest('GET', `/api/admin/ghost-players?period=${statsPeriod}`);
      const data = await res.json();
      setRawEmailRecipients(data);
      setBroadcastTarget("raw_emails");
      const tpl = EMAIL_TEMPLATES.find(t => t.id === "ghost_player")!;
      setBroadcastSubject(tpl.subject);
      setBroadcastMessage(tpl.message);
      setActiveTab("communications");
    } catch (e) {
      console.error("Failed to fetch ghost players", e);
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<{ id: string, name: string } | null>(null);
  const [deleteClubDialogOpen, setDeleteClubDialogOpen] = useState(false);
  const [clubToDelete, setClubToDelete] = useState<{ id: string, name: string } | null>(null);

  const handleDeleteClick = (id: string, name: string) => {
    setAccountToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (accountToDelete) {
      deleteAccountMutation.mutate(accountToDelete.id);
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-padel-green">Admin Login</CardTitle>
            <CardDescription>Enter your admin credentials</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...adminForm}>
              <form onSubmit={handleAdminLogin} className="space-y-4">
                <FormField
                  control={adminForm.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                          <Input
                            {...field}
                            type="text"
                            placeholder="Enter admin username"
                            className="pl-10"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={adminForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                          <Input
                            {...field}
                            type="password"
                            placeholder="Enter admin password"
                            className="pl-10"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full bg-padel-green hover:bg-padel-green-light"
                  disabled={adminLoginMutation.isPending}
                >
                  {adminLoginMutation.isPending ? "Logging in..." : "Login as Admin"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-padel-green">Admin Panel</h1>
          <Button onClick={handleLogout} variant="outline">
            <LogOut className="mr-2" size={16} />
            Logout
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="siteweb">Site web</TabsTrigger>
            <TabsTrigger value="players">Players Management</TabsTrigger>
            <TabsTrigger value="alerts">Alert Settings</TabsTrigger>
            <TabsTrigger value="recap">Monthly Recap</TabsTrigger>
            <TabsTrigger value="communications">Communications</TabsTrigger>
            <TabsTrigger value="clubs">Clubs</TabsTrigger>
            <TabsTrigger value="matchs">Matchs</TabsTrigger>
            <TabsTrigger value="ftt">FFT</TabsTrigger>
            <TabsTrigger value="deleted-players">Comptes supprimés</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <AdminDashboard
              stats={adminStats}
              isLoading={isLoadingStats}
              period={statsPeriod}
              onPeriodChange={setStatsPeriod}
              onRefresh={refetchStats}
              onContactExpired={handleContactExpired}
              onContactGhosts={handleContactGhosts}
              onContactActive={handleContactActive}
              onContactInactive={handleContactInactive}
              onContactNotifications={handleContactNotifications}
              onContactInscrits={handleContactInscrits}
            />
          </TabsContent>

          <TabsContent value="siteweb">
            <AdminWebsiteStats
              stats={websiteStats}
              isLoading={isLoadingWebsiteStats}
              period={statsPeriod > 0 ? statsPeriod : 30}
              onPeriodChange={setStatsPeriod}
              onRefresh={refetchWebsiteStats}
            />
          </TabsContent>

          <TabsContent value="matchs">
            <AdminMatchRevert />
          </TabsContent>

          <TabsContent value="players">
            <Card>
              <CardHeader>
                <CardTitle>Players Management</CardTitle>
                <CardDescription>View and manage all players</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      type="text"
                      placeholder="Search players by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  <div className="flex flex-wrap gap-3 items-center">
                    <div className="flex items-center gap-2">
                      <Filter size={16} className="text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">Filters:</span>
                    </div>

                    <Select value={emailFilter} onValueChange={setEmailFilter}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="Email Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Email Status</SelectItem>
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="not-verified">Not Verified</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={suspendedFilter} onValueChange={setSuspendedFilter}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="Suspended Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Suspended Status</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                        <SelectItem value="not-suspended">Not Suspended</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={premiumFilter} onValueChange={setPremiumFilter}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="Premium Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Premium Status</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                        <SelectItem value="not-premium">Not Premium</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={statsFilter} onValueChange={setStatsFilter}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="Stats Alerts" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Players</SelectItem>
                        <SelectItem value="alert">Stats Watch Only</SelectItem>
                      </SelectContent>
                    </Select>

                    {(emailFilter !== "all" || suspendedFilter !== "all" || premiumFilter !== "all" || statsFilter !== "all") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEmailFilter("all");
                          setSuspendedFilter("all");
                          setPremiumFilter("all");
                          setStatsFilter("all");
                        }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-center py-8">Loading players...</div>
                ) : (
                  <div className="space-y-2">
                    {(filteredPlayers?.length || 0) === 0 && (players?.length || 0) > 0 && (
                      <div className="text-center py-8 text-gray-500">
                        No players match the selected filters
                      </div>
                    )}
                    {(filteredPlayers || []).map((player) => (
                      <Card key={player.id} className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg">{player.name}</h3>
                              {player.id.startsWith('user-') && (
                                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
                                  Pending Registration
                                </Badge>
                              )}
                              <div className="flex items-center gap-2">
                                {player.isPremium && (
                                  <Badge variant="default" className="bg-yellow-500">
                                    <Crown size={12} className="mr-1" />
                                    Premium {player.premiumType && `(${player.premiumType})`}
                                  </Badge>
                                )}
                                {player.user?.isSuspended && (
                                  <Badge variant="destructive">
                                    <Ban size={12} className="mr-1" />
                                    Suspended
                                  </Badge>
                                )}
                                {(() => {
                                  if (!player.statistics || !alertSettings) return null;
                                  const warnings = getActiveAlerts(player.statistics, alertSettings);

                                  if (warnings?.length > 0) {
                                    return (
                                      <div className="flex flex-col gap-1 ml-2">
                                        {(warnings || []).map((w, i) => (
                                          <Badge key={i} variant="destructive" className="bg-red-100 text-red-700 border-red-200 hover:bg-red-200 flex items-center gap-1 w-fit">
                                            <AlertTriangle size={12} className="text-red-600" />
                                            <span className="text-xs font-semibold">{w}</span>
                                          </Badge>
                                        ))}
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>Email: {player.user?.email || "N/A"}</p>
                              <p className="text-xs text-gray-400">
                                Dernière connexion : {player.user?.lastLoginAt
                                  ? (() => {
                                      const d = new Date(player.user.lastLoginAt);
                                      const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
                                      return diff === 0 ? "Aujourd'hui" : diff === 1 ? "Hier" : `Il y a ${diff}j`;
                                    })()
                                  : "Jamais enregistrée"}
                              </p>
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1">
                                  {player.user?.isVerified ? (
                                    <>
                                      <CheckCircle size={14} className="text-green-500" />
                                      <span className="text-green-600">Email Verified</span>
                                    </>
                                  ) : (
                                    <>
                                      <XCircle size={14} className="text-red-500" />
                                      <span className="text-red-600">Email Not Verified</span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  {player.user?.isPhoneVerified ? (
                                    <>
                                      <CheckCircle size={14} className="text-green-500" />
                                      <span className="text-green-600">Phone Verified</span>
                                    </>
                                  ) : (
                                    <>
                                      <XCircle size={14} className="text-red-500" />
                                      <span className="text-red-600">Phone Not Verified</span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  {player.user?.isSuspended ? (
                                    <>
                                      <Ban size={14} className="text-red-500" />
                                      <span className="text-red-600">Suspended</span>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle size={14} className="text-green-500" />
                                      <span className="text-green-600">Not Suspended</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <p>ELO: {player.eloRating} | Matches: {player.matchesPlayed} (W: {player.matchesWon} / L: {player.matchesLost})</p>
                              <p className="text-xs text-gray-400 font-mono select-all">User ID: {player.user?.id ?? "—"}</p>

                              {player.assimilatedElo && (
                                <p className="text-sm text-padel-green font-medium">
                                  FTT Assimilation: {player.assimilatedElo} ELO (Rank: {player.fttRank})
                                </p>
                              )}

                              {player.user?.isSuspended && player.user.suspensionReason && (
                                <p className="text-red-600">Suspension Reason: {player.user.suspensionReason}</p>
                              )}
                              {/* New: Display Current Spree with color coding */}
                              {player.statistics?.currentSpree !== undefined && (
                                <p className={`text-sm ${player.statistics.currentSpree > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                  Current Spree: {player.statistics.currentSpree > 0 ? '+' : ''}{player.statistics.currentSpree}
                                </p>
                              )}

                              {/* Email Preferences */}
                              {player.user && (
                                <div className="flex items-center gap-2 mt-2">
                                  <Switch
                                    checked={player.user.acceptsMarketingEmails === true}
                                    onCheckedChange={(checked) => updatePreferencesMutation.mutate({ userId: player.user!.id, acceptsMarketingEmails: checked })}
                                    disabled={updatePreferencesMutation.isPending}
                                    id={`pref-${player.id}`}
                                  />
                                  <Label htmlFor={`pref-${player.id}`} className="text-xs text-gray-600 cursor-pointer">
                                    Receives Marketing/Recap Emails
                                  </Label>
                                </div>
                              )}
                            </div>
                            {!player.user?.isVerified && (
                              <div className="mt-3">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                                  onClick={() => {
                                    // For pending registrations, use player.id (which starts with "pending-")
                                    // For existing users, use user.id
                                    const accountId = player.id.startsWith('pending-') || player.id.startsWith('user-')
                                      ? player.id
                                      : (player.user?.id || player.id);
                                    activateAccountMutation.mutate(accountId);
                                  }}
                                  disabled={activateAccountMutation.isPending}
                                >
                                  <Check size={14} className="mr-1" />
                                  {activateAccountMutation.isPending ? "Activating..." : "Activate Account"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-red-50 text-red-700 border-red-300 hover:bg-red-100 ml-2"
                                  onClick={() => {
                                    const accountId = player.id.startsWith('pending-') || player.id.startsWith('user-')
                                      ? player.id
                                      : (player.user?.id || player.id);
                                    handleDeleteClick(accountId, player.name);
                                  }}
                                  disabled={deleteAccountMutation.isPending}
                                >
                                  <LogOut size={14} className="mr-1" />
                                  {deleteAccountMutation.isPending ? "Deleting..." : "Delete Account"}
                                </Button>
                              </div>
                            )}
                          </div>
                          {!player.id.startsWith('pending-') && !player.id.startsWith('user-') && (
                            <div className="flex flex-col gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOpenPremiumDialog(player)}
                                className="w-full whitespace-nowrap"
                              >
                                <Settings size={14} className="mr-1" />
                                Premium
                              </Button>
                              {player.user?.id && (
                                player.user.isSuspended ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="bg-green-50 text-green-700 border-green-300 hover:bg-green-100 w-full whitespace-nowrap"
                                    onClick={() => unsuspendAccountMutation.mutate(player.user!.id)}
                                    disabled={unsuspendAccountMutation.isPending}
                                  >
                                    <CheckCircle size={14} className="mr-1" />
                                    {unsuspendAccountMutation.isPending ? "Unsuspending..." : "Unsuspend"}
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="bg-red-50 text-red-700 border-red-300 hover:bg-red-100 w-full whitespace-nowrap"
                                    onClick={() => handleOpenSuspensionDialog(player)}
                                  >
                                    <Ban size={14} className="mr-1" />
                                    Suspend
                                  </Button>
                                )
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOpenEloDialog(player)}
                                className="w-full whitespace-nowrap bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100"
                              >
                                Edit ELO
                              </Button>
                            </div>
                          )}
                        </div>
                      </Card>
                    ))}
                    {(players?.length || 0) === 0 && (
                      <div className="text-center py-8 text-gray-500">No players found</div>
                    )}
                    {(filteredPlayers?.length || 0) > 0 && (
                      <div className="text-center py-4 text-sm text-gray-500">
                        Showing {filteredPlayers?.length || 0} of {players?.length || 0} players
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="alerts">
            <Card>
              <CardHeader>
                <CardTitle>Statistics Alerts Configuration</CardTitle>
                <CardDescription>Configure thresholds for automatic email alerts when player statistics exceed limits.</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingAlerts ? (
                  <div className="text-center py-8">Loading settings...</div>
                ) : (
                  <AlertSettingsForm
                    settings={alertSettings}
                    onSave={(data) => updateAlertSettingsMutation.mutate(data)}
                    isLoading={updateAlertSettingsMutation.isPending}
                    playerCount={players?.length || 0}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="recap">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Recap Testing</CardTitle>
                <CardDescription>Manually trigger the monthly recap process for testing purposes.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col gap-4">
                  <div className="border p-4 rounded-md bg-white">
                    <h3 className="font-semibold text-lg mb-2">1. Generate Snapshots</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Calculates player and club statistics for the current month (or specified date) and stores them in the `monthly_player_stats` and `monthly_club_rankings` tables.
                    </p>
                    <Button
                      onClick={() => triggerRecapSnapshotMutation.mutate(undefined)}
                      disabled={triggerRecapSnapshotMutation.isPending}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {triggerRecapSnapshotMutation.isPending ? "Generating Snapshots..." : "Trigger Snapshot Generation"}
                    </Button>
                  </div>

                  <div className="border p-4 rounded-md bg-white">
                    <h3 className="font-semibold text-lg mb-2">2. Send Recap Emails</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Generates the infographic image and sends the recap email to all verified users based on the snapshots.
                    </p>

                    <div className="space-y-4 mb-4">
                      <Label>Target Audience</Label>
                      <RadioGroup value={recapTarget} onValueChange={(v) => setRecapTarget(v as "all" | "specific")} className="flex flex-col space-y-2">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="all" id="recap-target-all" />
                          <Label htmlFor="recap-target-all" className="cursor-pointer">All eligible users (verified, premium, ≥4 matches)</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="specific" id="recap-target-specific" />
                          <Label htmlFor="recap-target-specific" className="cursor-pointer">Specific eligible users</Label>
                        </div>
                      </RadioGroup>

                      {recapTarget === "specific" && (
                        <div className="mt-4 border rounded-md p-4 bg-white">
                          <div className="flex justify-between items-center mb-2">
                            <Label>Select Users ({recapSelectedUserIds.length} selected)</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const allValidIds = (players || [])
                                  .filter(p => {
                                    if (!p.user?.isVerified || !p.isPremium || p.user?.acceptsMarketingEmails === false) return false;
                                    if (recapUserSearchQuery) {
                                      const q = recapUserSearchQuery.toLowerCase();
                                      return p.name.toLowerCase().includes(q) || p.user!.email.toLowerCase().includes(q);
                                    }
                                    return true;
                                  })
                                  .map(p => p.user!.id);

                                if (recapSelectedUserIds.length === allValidIds.length && allValidIds.length > 0) {
                                  setRecapSelectedUserIds([]);
                                } else {
                                  setRecapSelectedUserIds(allValidIds);
                                }
                              }}
                            >
                              Select All (Filtered)
                            </Button>
                          </div>
                          <div className="relative mb-3">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <Input
                              type="text"
                              placeholder="Search users to select..."
                              value={recapUserSearchQuery}
                              onChange={(e) => setRecapUserSearchQuery(e.target.value)}
                              className="pl-10 h-9"
                            />
                          </div>
                          <ScrollArea className="h-[200px] border rounded-md p-2">
                            <div className="space-y-2">
                              {(players || [])
                                .filter(p => {
                                  if (!p.user?.isVerified || !p.isPremium || p.user?.acceptsMarketingEmails === false) return false;
                                  if (recapUserSearchQuery) {
                                    const q = recapUserSearchQuery.toLowerCase();
                                    return p.name.toLowerCase().includes(q) || p.user!.email.toLowerCase().includes(q);
                                  }
                                  return true;
                                })
                                .map((player) => (
                                  <div key={player.id} className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`recap-user-${player.user!.id}`}
                                      checked={recapSelectedUserIds.includes(player.user!.id)}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setRecapSelectedUserIds(prev => [...prev, player.user!.id]);
                                        } else {
                                          setRecapSelectedUserIds(prev => prev.filter(id => id !== player.user!.id));
                                        }
                                      }}
                                    />
                                    <Label htmlFor={`recap-user-${player.user!.id}`} className="cursor-pointer text-sm font-normal">
                                      {player.name} ({player.user!.email})
                                    </Label>
                                  </div>
                                ))}
                              {(players || []).filter(p => {
                                if (!p.user?.isVerified || !p.isPremium || p.user?.acceptsMarketingEmails === false) return false;
                                if (recapUserSearchQuery) {
                                  const q = recapUserSearchQuery.toLowerCase();
                                  return p.name.toLowerCase().includes(q) || p.user!.email.toLowerCase().includes(q);
                                }
                                return true;
                              }).length === 0 && (
                                  <div className="text-center text-gray-500 py-4 text-sm">No eligible users match your search.</div>
                                )}
                            </div>
                          </ScrollArea>
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={() => {
                        const payload: any = { date: undefined };
                        if (recapTarget === "specific") {
                          payload.userIds = recapSelectedUserIds;
                        }
                        triggerRecapEmailMutation.mutate(payload);
                      }}
                      disabled={triggerRecapEmailMutation.isPending || (recapTarget === "specific" && recapSelectedUserIds.length === 0)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Mail className="mr-2" size={16} />
                      {triggerRecapEmailMutation.isPending ? "Sending Emails..." : "Trigger Email Processing"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="communications">
            <Card className="mb-4">
              <CardHeader>
                <CardTitle>Générateur de lien UTM</CardTitle>
                <CardDescription>Créez un lien tracké pour suivre la provenance des inscriptions.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label>Source <span className="text-gray-400 font-normal text-xs">(ex: instagram, newsletter)</span></Label>
                    <Input value={utmSource} onChange={e => setUtmSource(e.target.value)} placeholder="instagram" />
                  </div>
                  <div className="space-y-1">
                    <Label>Medium <span className="text-gray-400 font-normal text-xs">(ex: bio, email, qrcode)</span></Label>
                    <Input value={utmMedium} onChange={e => setUtmMedium(e.target.value)} placeholder="bio" />
                  </div>
                  <div className="space-y-1">
                    <Label>Campagne <span className="text-gray-400 font-normal text-xs">(optionnel)</span></Label>
                    <Input value={utmCampaign} onChange={e => setUtmCampaign(e.target.value)} placeholder="été-2026" />
                  </div>
                </div>
                {utmSource && (
                  <div className="space-y-2">
                    <Label>Lien généré</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-gray-100 rounded px-3 py-2 break-all select-all">
                        {`${window.location.origin}/login?utm_source=${encodeURIComponent(utmSource)}${utmMedium ? `&utm_medium=${encodeURIComponent(utmMedium)}` : ""}${utmCampaign ? `&utm_campaign=${encodeURIComponent(utmCampaign)}` : ""}`}
                      </code>
                      <Button variant="outline" size="sm" onClick={() => {
                        const url = `${window.location.origin}/login?utm_source=${encodeURIComponent(utmSource)}${utmMedium ? `&utm_medium=${encodeURIComponent(utmMedium)}` : ""}${utmCampaign ? `&utm_campaign=${encodeURIComponent(utmCampaign)}` : ""}`;
                        navigator.clipboard.writeText(url).then(() => {
                          setUtmCopied(true);
                          setTimeout(() => setUtmCopied(false), 2000);
                        });
                      }}>
                        {utmCopied ? "Copié ✓" : "Copier"}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Broadcast Email</CardTitle>
                <CardDescription>Send an email to all users who have opted in to marketing communications.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Template</Label>
                  <Select onValueChange={(id) => {
                    const tpl = EMAIL_TEMPLATES.find(t => t.id === id);
                    if (tpl) { setBroadcastSubject(tpl.subject); setBroadcastMessage(tpl.message); }
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir un template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {EMAIL_TEMPLATES.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="broadcast-subject">Subject</Label>
                  <Input
                    id="broadcast-subject"
                    value={broadcastSubject}
                    onChange={(e) => setBroadcastSubject(e.target.value)}
                    placeholder="Enter email subject"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="broadcast-message">Message</Label>
                  <Textarea
                    id="broadcast-message"
                    value={broadcastMessage}
                    onChange={(e) => setBroadcastMessage(e.target.value)}
                    placeholder="Enter email message (newlines will be converted to paragraphs/breaks)"
                    className="min-h-[200px]"
                  />
                </div>
                <div className="space-y-4 pt-4 border-t">
                  <Label>Target Audience</Label>
                  <RadioGroup value={broadcastTarget} onValueChange={(v) => setBroadcastTarget(v as "all" | "specific" | "raw_emails")} className="flex flex-col space-y-2">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="all" id="target-all" />
                      <Label htmlFor="target-all" className="cursor-pointer">All verified opted-in community users</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="specific" id="target-specific" />
                      <Label htmlFor="target-specific" className="cursor-pointer">Specific users</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="raw_emails" id="target-raw" />
                      <Label htmlFor="target-raw" className="cursor-pointer">Adresses email spécifiques</Label>
                    </div>
                  </RadioGroup>

                  {broadcastTarget === "raw_emails" && (
                    <div className="mt-4 border rounded-md p-4 bg-orange-50">
                      <Label className="mb-2 block">Destinataires ({rawEmailRecipients.length})</Label>
                      {rawEmailRecipients.length === 0 ? (
                        <p className="text-xs text-gray-400">Aucun destinataire. Utilisez l'icône email sur la carte du dashboard.</p>
                      ) : (
                        <div className="space-y-1">
                          {rawEmailRecipients.map((r, i) => (
                            <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-orange-100">
                              <span className="font-medium text-gray-700">{r.first_name} {r.last_name} <span title={r.language}>{langFlag(r.language)}</span></span>
                              <span className="text-gray-500 text-xs">{r.email}</span>
                              <button onClick={() => setRawEmailRecipients(prev => prev.filter((_, j) => j !== i))} className="text-gray-300 hover:text-red-400 ml-2"><UserX size={13} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {broadcastTarget === "specific" && (
                    <div className="mt-4 border rounded-md p-4 bg-white">
                      <div className="flex justify-between items-center mb-2">
                        <Label>Select Users ({selectedUserIds.length} selected)</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const allValidIds = (players || [])
                              .filter(isBroadcastCandidate)
                              .map(p => p.user!.id);

                            if (selectedUserIds.length === allValidIds.length && allValidIds.length > 0) {
                              setSelectedUserIds([]);
                            } else {
                              setSelectedUserIds(allValidIds);
                            }
                          }}
                        >
                          Select All (Filtered)
                        </Button>
                      </div>
                      <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <Input
                          type="text"
                          placeholder="Search users to select... (lang:fr pour filtrer par langue)"
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                          className="pl-10 h-9"
                        />
                      </div>
                      <ScrollArea className="h-[200px] border rounded-md p-2">
                        <div className="space-y-2">
                          {(players || [])
                            .filter(isBroadcastCandidate)
                            .map((player) => (
                              <div key={player.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`user-${player.user!.id}`}
                                  checked={selectedUserIds.includes(player.user!.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedUserIds(prev => [...prev, player.user!.id]);
                                    } else {
                                      setSelectedUserIds(prev => prev.filter(id => id !== player.user!.id));
                                    }
                                  }}
                                />
                                <Label htmlFor={`user-${player.user!.id}`} className="cursor-pointer text-sm font-normal">
                                  <span title={player.user!.language}>{langFlag(player.user!.language)}</span> {player.name} <span className="text-gray-400">({player.user!.email})</span>
                                </Label>
                              </div>
                            ))}
                          {(players || []).filter(isBroadcastCandidate).length === 0 && (
                              <div className="text-center text-gray-500 py-4 text-sm">No users match your search.</div>
                            )}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleSendBroadcast}
                  disabled={
                    sendBroadcastEmailMutation.isPending ||
                    !broadcastSubject ||
                    !broadcastMessage ||
                    (broadcastTarget === "specific" && selectedUserIds.length === 0) ||
                    (broadcastTarget === "raw_emails" && rawEmailRecipients.length === 0)
                  }
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                >
                  <Mail className="mr-2" size={16} />
                  {sendBroadcastEmailMutation.isPending ? "Sending..." : "Send Broadcast Email"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="clubs">
            <div className="space-y-4">

              {/* Season Recap Send */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail size={18} className="text-orange-500" />
                    Send Season Recap Email
                  </CardTitle>
                  <CardDescription>Manually send a season recap email to all eligible members of a club for a specific archived season.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Club</Label>
                      <Select value={selectedRecapClubId} onValueChange={(v) => { setSelectedRecapClubId(v); setSelectedRecapSeasonId(""); }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a club..." />
                        </SelectTrigger>
                        <SelectContent>
                          {(adminClubs || []).filter((c: any) => (c.archivedSeasons?.length || 0) > 0).map((club: any) => (
                            <SelectItem key={club.id} value={club.id}>{club.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Archived Season</Label>
                      <Select
                        value={selectedRecapSeasonId}
                        onValueChange={setSelectedRecapSeasonId}
                        disabled={!selectedRecapClubId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a season..." />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedRecapClubId && (() => {
                            const club = (adminClubs || []).find((c: any) => c.id === selectedRecapClubId);
                            const seasons = [...(club?.archivedSeasons || [])].sort((a: any, b: any) =>
                              (b.name?.split(' - ')[0] || '').localeCompare(a.name?.split(' - ')[0] || '')
                            );
                            return seasons.map((s: any) => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {selectedRecapClubId && selectedRecapSeasonId && (() => {
                    const club = (adminClubs || []).find((c: any) => c.id === selectedRecapClubId);
                    const season = (club?.archivedSeasons || []).find((s: any) => s.id === selectedRecapSeasonId);
                    if (!season) return null;
                    return (
                      <div className="bg-orange-50 border border-orange-200 rounded-md p-3 text-sm">
                        <p className="font-medium text-orange-800">📅 {season.name}</p>
                        <p className="text-orange-700 mt-1">{season.rankings?.length || 0} players in the archive</p>
                      </div>
                    );
                  })()}

                  <Button
                    onClick={() => {
                      if (!selectedRecapClubId || !selectedRecapSeasonId) return;
                      const club = (adminClubs || []).find((c: any) => c.id === selectedRecapClubId);
                      const season = (club?.archivedSeasons || []).find((s: any) => s.id === selectedRecapSeasonId);
                      if (!season) return;
                      sendSeasonRecapMutation.mutate({
                        clubId: selectedRecapClubId,
                        seasonId: selectedRecapSeasonId,
                        seasonStart: season.startDate,
                        seasonEnd: season.archivedAt
                      });
                    }}
                    disabled={!selectedRecapClubId || !selectedRecapSeasonId || sendSeasonRecapMutation.isPending}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    <Mail size={16} className="mr-2" />
                    {sendSeasonRecapMutation.isPending ? "Sending..." : "Send Season Recap"}
                  </Button>
                </CardContent>
              </Card>

              {/* Club list / verification */}
              <Card>
                <CardHeader>
                  <CardTitle>Clubs Management</CardTitle>
                  <CardDescription>View all clubs and manage their verified status.</CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingClubs ? (
                    <div className="text-center py-8">Loading clubs...</div>
                  ) : (
                    <div className="space-y-4">
                      {(adminClubs?.length || 0) === 0 ? (
                        <div className="text-center py-8 text-gray-500">No clubs found</div>
                      ) : (
                        (adminClubs || []).map((club: any) => (
                          <Card key={club.id} className="p-4 flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-lg">{club.name}</h3>
                                {club.isVerified && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger>
                                        <BadgeCheck className="text-blue-500 fill-blue-500/20" size={20} />
                                      </TooltipTrigger>
                                      <TooltipContent>Verified Club</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mb-2">
                                {club.description || "No description"}
                              </p>
                              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                                <Badge variant="outline">{club.memberCount} members</Badge>
                                {(club.archivedSeasons?.length || 0) > 0 && (
                                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                    {club.archivedSeasons.length} archived season{club.archivedSeasons.length > 1 ? 's' : ''}
                                  </Badge>
                                )}
                                <span className="flex items-center">
                                  Created by: {club.creatorName}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <Label htmlFor={`verify-${club.id}`} className="text-sm cursor-pointer border px-3 py-1 rounded-md bg-gray-50">
                                {club.isVerified ? "Verified" : "Unverified"}
                              </Label>
                              <Switch
                                id={`verify-${club.id}`}
                                checked={club.isVerified}
                                onCheckedChange={(checked) => {
                                  updateClubVerificationMutation.mutate({ clubId: club.id, isVerified: checked });
                                }}
                                disabled={updateClubVerificationMutation.isPending}
                              />
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setClubToDelete({ id: club.id, name: club.name });
                                  setDeleteClubDialogOpen(true);
                                }}
                                disabled={deleteClubAdminMutation.isPending}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </Card>
                        ))
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

            </div>
          </TabsContent>

          <TabsContent value="ftt">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database size={20} className="text-padel-green" />
                    FFT Database Status
                  </CardTitle>
                  <CardDescription>Real-time connectivity monitoring of the remote MySQL database.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 p-4 border rounded-lg bg-white">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-full ${fttStatus?.connected ? 'bg-green-100' : 'bg-red-100'}`}>
                        {fttStatus?.connected ? (
                          <CheckCircle className="text-green-600" size={24} />
                        ) : (
                          <XCircle className="text-red-600" size={24} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg">
                            {fttStatus?.connected ? "Connected" : "Disconnected"}
                          </span>
                          <Badge variant={fttStatus?.connected ? "default" : "destructive"} className={fttStatus?.connected ? "bg-green-500" : ""}>
                            {fttStatus?.connected ? "Online" : "Offline"}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">
                          Last monitoring: {fttStatus?.lastCheck ? new Date(fttStatus.lastCheck).toLocaleString() : "Pending (runs every hour from 1st Tuesday of month)"}
                        </p>
                        {fttStatus?.lastScrapedAt && (
                          <p className="text-sm text-gray-500 mt-0.5">
                            Last FFT update: {new Date(fttStatus.lastScrapedAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                    {fttStatus?.error && (
                      <div className="flex items-center gap-2 text-red-600 bg-red-50 p-2 rounded border border-red-100">
                        <AlertTriangle size={16} />
                        <span className="text-sm font-medium">{fttStatus.error}</span>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <Switch
                        id="ftt-auto-sync"
                        checked={fttSettings?.autoSyncEnabled ?? false}
                        onCheckedChange={(checked) => updateFttSettingsMutation.mutate({ autoSyncEnabled: checked })}
                        disabled={updateFttSettingsMutation.isPending}
                      />
                      <label htmlFor="ftt-auto-sync" className="text-sm font-medium cursor-pointer">
                        Auto-sync (hourly, from 1st Tuesday of month)
                      </label>
                    </div>
                    <Button
                      onClick={() => syncPlayersWithFttMutation.mutate()}
                      disabled={syncPlayersWithFttMutation.isPending}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold min-w-[150px]"
                      variant="default"
                    >
                      {syncPlayersWithFttMutation.isPending ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 h-4 w-4" />
                      )}
                      {syncPlayersWithFttMutation.isPending ? "Syncing..." : "Sync Players"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search size={20} className="text-padel-green" />
                    Progressive Ranking Search
                  </CardTitle>
                  <CardDescription>Search for any player in the official FFT database (120k+ entries).</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      type="text"
                      placeholder="Type a name or surname (min 2 chars)..."
                      value={fttSearchQuery}
                      onChange={(e) => setFttSearchQuery(e.target.value)}
                      className="pl-10 h-12 text-lg"
                    />
                  </div>

                  {isSearchingFtt ? (
                    <div className="text-center py-8 text-gray-500 italic">Searching database...</div>
                  ) : fttSearchQuery.length >= 2 ? (
                    <div className="border rounded-lg overflow-hidden bg-white">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-700 font-medium border-b">
                          <tr>
                            <th className="px-4 py-3">Rank</th>
                            <th className="px-4 py-3">Last Name</th>
                            <th className="px-4 py-3">First Name</th>
                            <th className="px-4 py-3">ELO PadELO</th>
                            <th className="px-4 py-3">Evolution</th>
                            <th className="px-4 py-3">Tournaments</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {fttSearchResults.length > 0 ? (
                            fttSearchResults.map((result: any, i: number) => {
                              const evo = parseInt(result.evolution) || 0;
                              const isPositive = result.evolution && !result.evolution.startsWith('-');
                              const evoValue = result.evolution ? result.evolution.replace(/^[+-]/, '') : '0';

                              return (
                                <tr key={i} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-4 py-3 font-bold text-padel-green">#{result.rank}</td>
                                  <td className="px-4 py-3 uppercase">{result.nom}</td>
                                  <td className="px-4 py-3 capitalize">{result.prenom}</td>
                                  <td className="px-4 py-3">
                                    {result.localElo ? (
                                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 font-bold">
                                        {result.localElo}
                                      </Badge>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    {result.evolution ? (
                                      <div className={`flex items-center gap-1 font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                        {isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                        {isPositive ? '+' : '-'}{evoValue}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600">{result.tournaments || 0}</td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                No results found for "{fttSearchQuery}"
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      
                      {hasMoreFtt && (
                        <div className="p-4 border-t bg-gray-50 flex justify-center">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={loadMoreFttResults}
                            disabled={isSearchingFtt}
                            className="text-padel-green hover:text-padel-green hover:bg-green-100 font-semibold flex items-center gap-2"
                          >
                            {isSearchingFtt ? (
                              <RefreshCw size={14} className="animate-spin" />
                            ) : (
                              <ChevronDown size={16} />
                            )}
                            Afficher les autres
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 border-2 border-dashed rounded-lg bg-gray-50">
                      <p className="text-gray-400">Enter at least 2 characters to start searching the FFT database.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="deleted-players">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trash2Icon size={18} className="text-red-700" />
                  Comptes supprimés (UserDEL)
                </CardTitle>
                <CardDescription>
                  Joueurs anonymisés suite à une suppression de compte. Vous pouvez rattacher leur historique à un nouveau compte utilisateur.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingDeletedPlayers ? (
                  <div className="text-center py-8 text-gray-400">Chargement...</div>
                ) : deletedPlayers.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">Aucun compte supprimé.</div>
                ) : (
                  <div className="space-y-2">
                    {deletedPlayers.map((p: any) => (
                      <div key={p.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                        <div className="flex flex-col gap-1 text-sm">
                          <span className="font-mono text-xs text-gray-400">{p.id}</span>
                          <div className="flex gap-4 text-gray-700">
                            <span>ELO : <strong>{p.elo_rating}</strong></span>
                            <span>Parties : <strong>{p.matches_played}</strong></span>
                            <span>V/D : {p.matches_won}/{p.matches_lost}</span>
                            {p.last_match_at && (
                              <span className="text-gray-400">
                                Dernier match : {new Date(p.last_match_at).toLocaleDateString("fr-FR")}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {p.matches_played === 0 && (
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={deleteUserDelMutation.isPending}
                              onClick={() => {
                                if (confirm("Supprimer définitivement ce UserDEL (0 partie) ?")) {
                                  deleteUserDelMutation.mutate(p.id);
                                }
                              }}
                            >
                              Supprimer
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-600 text-green-700 hover:bg-green-50"
                            onClick={() => {
                              setSelectedDeletedPlayer(p);
                              setRestoreUserId("");
                              setRestoreName("");
                              setRestoreDialogOpen(true);
                            }}
                          >
                            Restaurer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Restore Deleted Player Dialog */}
        <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Restaurer un compte supprimé</DialogTitle>
              <DialogDescription>
                {selectedDeletedPlayer && (
                  <>Rattacher le joueur (ELO {selectedDeletedPlayer.elo_rating}, {selectedDeletedPlayer.matches_played} parties) à un nouveau compte.</>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>ID du nouvel utilisateur</Label>
                <Input
                  placeholder="UUID du compte (ex: abc123-...)"
                  value={restoreUserId}
                  onChange={(e) => setRestoreUserId(e.target.value)}
                />
                <p className="text-xs text-gray-400">Visible dans l'onglet Players Management</p>
              </div>
              <div className="space-y-2">
                <Label>Nom du joueur</Label>
                <Input
                  placeholder="Prénom Nom"
                  value={restoreName}
                  onChange={(e) => setRestoreName(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRestoreDialogOpen(false)}>Annuler</Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={!restoreUserId.trim() || !restoreName.trim() || restoreDeletedPlayerMutation.isPending}
                onClick={() => {
                  if (!selectedDeletedPlayer) return;
                  restoreDeletedPlayerMutation.mutate({
                    playerId: selectedDeletedPlayer.id,
                    userId: restoreUserId.trim(),
                    name: restoreName.trim(),
                  });
                }}
              >
                {restoreDeletedPlayerMutation.isPending ? "Restauration..." : "Confirmer la restauration"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Premium Configuration Dialog */}
        <Dialog open={premiumDialogOpen} onOpenChange={setPremiumDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Manage Premium Subscription</DialogTitle>
              <DialogDescription>
                {selectedPlayer && `Configure premium for ${selectedPlayer.name}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="premium-type">Premium Type</Label>
                <Select
                  value={premiumType}
                  onValueChange={(value) => {
                    setPremiumType(value);
                    if (value !== "none" && value !== "") {
                      setPremiumEndDate(calculateEndDate(value));
                    } else {
                      setPremiumEndDate("");
                    }
                  }}
                >
                  <SelectTrigger id="premium-type">
                    <SelectValue placeholder="Select premium type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Premium (Disable)</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                    <SelectItem value="lifetime">Lifetime</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {premiumType && premiumType !== "none" && (
                <div className="space-y-2">
                  <Label htmlFor="premium-end-date">End Date (optional)</Label>
                  <Input
                    id="premium-end-date"
                    type="date"
                    value={premiumEndDate}
                    onChange={(e) => setPremiumEndDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-gray-500">
                    Leave empty to use default duration. For lifetime, this field is ignored.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setPremiumDialogOpen(false);
                  setSelectedPlayer(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdatePremium}
                disabled={updatePremiumMutation.isPending}
                className="bg-padel-green hover:bg-padel-green-light"
              >
                {updatePremiumMutation.isPending ? "Updating..." : "Update Premium"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Suspension Configuration Dialog */}
        <Dialog open={suspensionDialogOpen} onOpenChange={setSuspensionDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Suspend Account</DialogTitle>
              <DialogDescription>
                {selectedPlayer && `Suspend account for ${selectedPlayer.name}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedPlayer?.user?.isSuspended && selectedPlayer.user.suspensionReason && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm font-semibold text-red-800 mb-1">Current Suspension Reason:</p>
                  <p className="text-sm text-red-700">{selectedPlayer.user.suspensionReason}</p>
                  {selectedPlayer.user.suspendedUntil && (
                    <p className="text-xs text-red-600 mt-1">
                      Suspended until: {new Date(selectedPlayer.user.suspendedUntil).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="suspension-reason">Reason for Suspension</Label>
                <Input
                  id="suspension-reason"
                  type="text"
                  placeholder="Enter reason for suspension"
                  value={suspensionReason}
                  onChange={(e) => setSuspensionReason(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="suspended-until">Suspended Until (optional)</Label>
                <Input
                  id="suspended-until"
                  type="date"
                  value={suspendedUntil}
                  onChange={(e) => setSuspendedUntil(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-gray-500">
                  Leave empty for permanent suspension. Set a date for temporary suspension.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setSuspensionDialogOpen(false);
                  setSelectedPlayer(null);
                  setSuspensionReason("");
                  setSuspendedUntil("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSuspendAccount}
                disabled={suspendAccountMutation.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {suspendAccountMutation.isPending ? "Suspending..." : "Suspend Account"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ELO Configuration Dialog */}
        <Dialog open={eloDialogOpen} onOpenChange={setEloDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit ELO Rating</DialogTitle>
              <DialogDescription>
                {selectedPlayer && `Modify ELO rating for ${selectedPlayer.name}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedPlayer?.assimilatedElo && selectedPlayer?.fttRank && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <p className="text-sm font-semibold text-green-800 mb-1">FTT Intelligence:</p>
                  <p className="text-sm text-green-700">Rank: {selectedPlayer.fttRank}</p>
                  <p className="text-sm text-green-700">Recommended Baseline: {selectedPlayer.assimilatedElo} ELO</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="elo-rating">New ELO Score</Label>
                <Input
                  id="elo-rating"
                  type="number"
                  placeholder="Enter ELO score"
                  value={newEloRating}
                  onChange={(e) => setNewEloRating(e.target.value === "" ? "" : Number(e.target.value))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setEloDialogOpen(false);
                  setSelectedPlayer(null);
                  setNewEloRating("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (selectedPlayer && newEloRating !== "") {
                    updateEloMutation.mutate({
                      playerId: selectedPlayer.id,
                      eloRating: Number(newEloRating)
                    });
                  }
                }}
                disabled={updateEloMutation.isPending || newEloRating === ""}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {updateEloMutation.isPending ? "Applying..." : "Apply Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Account Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Account</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete the account for <strong>{accountToDelete?.name}</strong>?
                This action cannot be undone. All associated data will be permanently removed.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setAccountToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deleteAccountMutation.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteAccountMutation.isPending ? "Deleting..." : "Delete Account"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Club Dialog */}
        <Dialog open={deleteClubDialogOpen} onOpenChange={setDeleteClubDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Supprimer le club</DialogTitle>
              <DialogDescription>
                Voulez-vous vraiment supprimer définitivement le club <strong>{clubToDelete?.name}</strong> ?
                Cette action est irréversible. Tous les membres et données associées seront supprimés.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteClubDialogOpen(false);
                  setClubToDelete(null);
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={() => {
                  if (clubToDelete) {
                    deleteClubAdminMutation.mutate(clubToDelete.id);
                    setDeleteClubDialogOpen(false);
                    setClubToDelete(null);
                  }
                }}
                disabled={deleteClubAdminMutation.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteClubAdminMutation.isPending ? "Suppression..." : "Supprimer le club"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div >
  );
}
